import { useArrayFilter, useLocalStorage } from "@vueuse/core";
import { useBuiltRewardSuppliers } from "../store/reward/supplier/composables";
import { useBuiltComponentsFromIds } from "../store/component-built/composables";
import { computed } from "vue";
import { useNavigationContext } from "./navigationContext";
import Session from "Session";
const EXTOLE_NAVBAR_IS_COLLAPSED = "extole_navbar_is_collapsed";
const EXTOLE_SECONDARY_MENU_PARENT = "extole_secondary_menu_parent";
export const useExtoleNavbarToggle = () => {
    const isNavbarCollapsed = useLocalStorage(EXTOLE_NAVBAR_IS_COLLAPSED, false);
    return {
        isNavbarCollapsed,
        toggleNavbar() {
            isNavbarCollapsed.value = !isNavbarCollapsed.value;
        },
    };
};
let singletonInstance = null;
export const useSecondaryMenuParent = () => {
    if (singletonInstance !== null) {
        return singletonInstance;
    }
    const session = Session.getInstance();
    const isUserSupportUser = session.isUserSupportUser();
    const isSelfServiceUser = session.isSelfServiceUser();
    let defaultEntryItem = "programs";
    if (isUserSupportUser) {
        defaultEntryItem = "user-support";
    }
    else if (isSelfServiceUser) {
        defaultEntryItem = "self-service";
    }
    const pathToParentMap = {
        "/programs": "programs",
        "/account": "performance",
        "/audiences-overview": "audiences",
        "/account-rewards": "rewards",
        "/reports": "reports",
        "/integrations": "partners",
        "/tech-center": "tech-center",
        "/security-center": "security-center",
        "/profiles/search": "user-support",
        "/profiles/view": "user-support",
        "/profiles": "user-support",
        "/events/approvals": "user-support",
        "/events/live": "user-support",
        "/create-event": "user-support",
    };
    const navigationContext = useNavigationContext();
    const getParentForCurrentPath = () => {
        const currentPath = window.location.pathname;
        const isExternalPage = [
            "/settings",
            "/notifications",
            "/invite",
        ].includes(currentPath);
        if (isExternalPage) {
            return null;
        }
        // First, check navigation context for state-based tracking
        const fullUrl = window.location.pathname + window.location.hash;
        const contextParent = navigationContext.getContextForUrl(fullUrl) ||
            navigationContext.getContextForUrl(currentPath);
        if (contextParent) {
            return contextParent;
        }
        // Fallback to URL-based matching
        let matchedParent = undefined;
        matchedParent = pathToParentMap[currentPath];
        if (!matchedParent) {
            const possiblePrefixes = Object.keys(pathToParentMap)
                .filter((key) => currentPath.startsWith(key))
                .sort((a, b) => b.length - a.length);
            if (possiblePrefixes.length > 0) {
                matchedParent = pathToParentMap[possiblePrefixes[0]];
            }
        }
        if (!matchedParent) {
            const pathStart = "/" + currentPath.split("/").filter(Boolean)[0];
            matchedParent = pathToParentMap[pathStart];
        }
        return matchedParent || defaultEntryItem;
    };
    const secondaryMenuParent = useLocalStorage(EXTOLE_SECONDARY_MENU_PARENT, defaultEntryItem);
    const updateFromCurrentPath = () => {
        const newParent = getParentForCurrentPath();
        if (newParent !== secondaryMenuParent.value) {
            secondaryMenuParent.value = newParent;
        }
    };
    updateFromCurrentPath();
    const instance = {
        secondaryMenuParent,
        updateFromCurrentPath,
    };
    singletonInstance = instance;
    return instance;
};
export const useRewardBanks = () => {
    const { rewardSuppliers } = useBuiltRewardSuppliers({
        include_disabled: true,
    });
    const collectibleRewardSuppliers = useArrayFilter(rewardSuppliers, (rewardSupplier) => {
        return (rewardSupplier.display_type
            .toLowerCase()
            .includes("collectible") ||
            rewardSupplier.display_type.toLowerCase().includes("bankable"));
    });
    const componentIdsOfCollectibleRewardSuppliers = computed(() => {
        const output = new Set();
        collectibleRewardSuppliers.value.forEach((rewardSupplier) => {
            rewardSupplier.component_ids.forEach((componentId) => {
                output.add(componentId);
            });
        });
        return Array.from(output);
    });
    const { builtComponents: rewardComponentsForRewardBanks } = useBuiltComponentsFromIds(componentIdsOfCollectibleRewardSuppliers);
    const allReferencedComponentIds = computed(() => {
        const ids = new Set();
        rewardComponentsForRewardBanks.value.forEach((component) => {
            if (component && component.component_ids) {
                component.component_ids.forEach((id) => ids.add(id));
            }
        });
        return Array.from(ids);
    });
    const { builtComponents: rewardBanks } = useBuiltComponentsFromIds(allReferencedComponentIds);
    return {
        rewardBanks,
    };
};
